/**
 * "Útlit síðu": Samfellt | Heil síða | Tvær síður — the sheet viewer's page layout (viewer/pageMode).
 * "Tvær síður" is offered only while two-up is possible (landscape, ≥ 1000 CSS px wide: the viewer
 * reports it through SheetViewerEnv.onTwoUpEligible). Labels from xl, icons with a name and a hint
 * below; hidden below 640 px (phones switch with a double-tap in the middle of the sheet). Each
 * segment is ≥ 44 × 44 pt; the radio names are stable (E2E: 'Tvær síður').
 */
import { BookOpen, File, GalleryVertical } from 'lucide-react';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { effectiveMode, setPageMode, usePageModePrefs, type PageMode } from './pageMode';

export function PageModeToggle({ twoUpEligible }: { twoUpEligible: boolean }) {
  const t = useT();
  const mode = effectiveMode(usePageModePrefs(), twoUpEligible);
  const items: { id: PageMode; label: string; hint: string; icon: typeof File }[] = [
    { id: 'scroll', label: t('viewer.mode_scroll'), hint: t('viewer.mode_scroll_hint'), icon: GalleryVertical },
    { id: 'page', label: t('viewer.mode_page'), hint: t('viewer.mode_page_hint'), icon: File },
  ];
  if (twoUpEligible) items.push({ id: 'twoUp', label: t('viewer.layout_two_up'), hint: t('viewer.layout_two_up_hint'), icon: BookOpen });
  return (
    <div role="radiogroup" aria-label={t('viewer.mode_label')} className="hidden shrink-0 rounded-xl bg-[var(--surface-2)] p-1 ring-1 ring-[var(--line)] sm:flex">
      {items.map((it) => {
        const on = mode === it.id;
        return (
          <button
            key={it.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={it.label}
            title={it.hint}
            onClick={() => setPageMode(it.id, twoUpEligible)}
            className={cx(
              'inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg px-2 text-[16px] font-semibold transition xl:px-3',
              on ? 'bg-[var(--surface)] text-[var(--accent)] shadow-sm' : 'text-[var(--muted)]',
            )}
          >
            <it.icon className="size-5" aria-hidden />
            <span className="hidden xl:inline" aria-hidden>
              {it.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
